import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { addProductAsync } from '../features/products/productsSlice';
import WholesalerNavbar from '../features/wholesaler/components/WholesalerNavbar';
import { categories, purityOptions } from '../data/options';
import Footer from '../features/components/Footer';
import { FaUpload, FaTrash, FaImage, FaVideo } from 'react-icons/fa';

const WholesalerAddProduct = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.products);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    weight: '',
    purity: '',
    making: '',
    quantity: '',
    category: '',
    coverImage: null,
    image2: null,
    image3: null,
    video: null
  });

  const [errors, setErrors] = useState({});
  const [previews, setPreviews] = useState({
    coverImage: null,
    image2: null,
    image3: null,
    video: null
  });

  const validateFile = (file, type) => {
    const imageTypes = ['image/jpeg', 'image/jpg', 'image/mp3'];
    const videoTypes = ['video/mp4'];
    
    if (type === 'image' && !imageTypes.includes(file.type)) {
      return 'Please upload a valid image file (jpg, jpeg, mp3)';
    }
    if (type === 'video' && !videoTypes.includes(file.type)) {
      return 'Please upload a valid video file (mp4)';
    }
    return null;
  };

  const handleFileUpload = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    const error = validateFile(file, type);
    if (error) {
      setErrors(prev => ({ ...prev, [type]: error }));
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      if (type === 'coverImage') {
        setFormData(prev => ({ ...prev, coverImage: file }));
        setPreviews(prev => ({ ...prev, coverImage: reader.result }));
      } else if (type === 'image2') {
        setFormData(prev => ({ ...prev, image2: file }));
        setPreviews(prev => ({ ...prev, image2: reader.result }));
      } else if (type === 'image3') {
        setFormData(prev => ({ ...prev, image3: file }));
        setPreviews(prev => ({ ...prev, image3: reader.result }));
      } else if (type === 'video') {
        setFormData(prev => ({ ...prev, video: file }));
        setPreviews(prev => ({ ...prev, video: reader.result }));
      }
      setErrors(prev => ({ ...prev, [type]: null }));
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveFile = (type) => {
    if (type === 'coverImage') {
      setFormData(prev => ({ ...prev, coverImage: null }));
      setPreviews(prev => ({ ...prev, coverImage: null }));
    } else if (type === 'image2') {
      setFormData(prev => ({ ...prev, image2: null }));
      setPreviews(prev => ({ ...prev, image2: null }));
    } else if (type === 'image3') {
      setFormData(prev => ({ ...prev, image3: null }));
      setPreviews(prev => ({ ...prev, image3: null }));
    } else if (type === 'video') {
      setFormData(prev => ({ ...prev, video: null }));
      setPreviews(prev => ({ ...prev, video: null }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name || !formData.category || !formData.coverImage || !formData.weight || !formData.quantity) {
      setErrors(prev => ({
        ...prev,
        name: !formData.name ? 'Product Name is required' : null,
        category: !formData.category ? 'Category is required' : null,
        coverImage: !formData.coverImage ? 'Thumbnail Image is required' : null,
        weight: !formData.weight ? 'Weight is required' : null,
        quantity: !formData.quantity ? 'Quantity is required' : null
      }));
      return;
    }

    try {
      await dispatch(addProductAsync(formData)).unwrap();
      navigate('/wholesaler/inventory');
    } catch (err) {
      console.error('Failed to add product:', err);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <WholesalerNavbar />
      <div className="pt-20 max-w-4xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Add New Product</h2>
            
            {error && (
              <div className="mb-4 p-4 bg-red-50 rounded-md">
                <p className="text-red-600">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Required Fields Section */}
              <div className="bg-yellow-50 p-4 rounded-md mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Required Fields</h3>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                      Product Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      id="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                      Category *
                    </label>
                    <select
                      name="category"
                      id="category"
                      required
                      value={formData.category}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
                    >
                      <option value="">Select a category</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.name.toLowerCase()}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                    {errors.category && (
                      <p className="mt-1 text-sm text-red-600">{errors.category}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="weight" className="block text-sm font-medium text-gray-700">
                      Weight (g) *
                    </label>
                    <input
                      type="number"
                      name="weight"
                      id="weight"
                      required
                      value={formData.weight}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
                    />
                    {errors.weight && (
                      <p className="mt-1 text-sm text-red-600">{errors.weight}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="quantity" className="block text-sm font-medium text-gray-700">
                      Quantity *
                    </label>
                    <input
                      type="number"
                      name="quantity"
                      id="quantity"
                      required
                      value={formData.quantity}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
                    />
                    {errors.quantity && (
                      <p className="mt-1 text-sm text-red-600">{errors.quantity}</p>
                    )}
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Thumbnail Image *
                    </label>
                    <div className="mt-1 flex items-center space-x-4">
                      <label className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-yellow-500 hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 cursor-pointer">
                        <FaUpload className="mr-2" />
                        Upload Thumbnail
                        <input
                          type="file"
                          className="hidden"
                          accept=".jpg,.jpeg,.mp3"
                          onChange={(e) => handleFileUpload(e, 'coverImage')}
                        />
                      </label>
                      {previews.coverImage && (
                        <div className="flex items-center space-x-2">
                          <FaImage className="text-green-500" />
                          <span className="text-sm text-gray-600">
                            {formData.coverImage.name} uploaded
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRemoveFile('coverImage')}
                            className="text-red-600 hover:text-red-800"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      )}
                    </div>
                    {errors.coverImage && (
                      <p className="mt-1 text-sm text-red-600">{errors.coverImage}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Optional Fields Section */}
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Optional Details</h3>
                
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="purity" className="block text-sm font-medium text-gray-700">
                      Purity
                    </label>
                    <select
                      name="purity"
                      id="purity"
                      value={formData.purity}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
                    >
                      <option value="">Select purity</option>
                      {purityOptions.map((purity) => (
                        <option key={purity.id} value={purity.name}>
                          {purity.name}K
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="making" className="block text-sm font-medium text-gray-700">
                      Making Charge (%)
                    </label>
                    <input
                      type="number"
                      name="making"
                      id="making"
                      value={formData.making}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <textarea
                    name="description"
                    id="description"
                    rows={4}
                    value={formData.description}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Additional Images
                  </label>
                  <div className="mt-1 space-y-4">
                    {/* Image 2 Upload */}
                    <div className="flex items-center space-x-4">
                      {!formData.image2 ? (
                        <label className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-yellow-500 hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 cursor-pointer">
                          <FaUpload className="mr-2" />
                          Upload Image 2
                          <input
                            type="file"
                            className="hidden"
                            accept=".jpg,.jpeg,.mp3"
                            onChange={(e) => handleFileUpload(e, 'image2')}
                          />
                        </label>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <FaImage className="text-green-500" />
                          <span className="text-sm text-gray-600">
                            {formData.image2.name} uploaded
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRemoveFile('image2')}
                            className="text-red-600 hover:text-red-800"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Image 3 Upload */}
                    <div className="flex items-center space-x-4">
                      {!formData.image3 ? (
                        <label className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-yellow-500 hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 cursor-pointer">
                          <FaUpload className="mr-2" />
                          Upload Image 3
                          <input
                            type="file"
                            className="hidden"
                            accept=".jpg,.jpeg,.mp3"
                            onChange={(e) => handleFileUpload(e, 'image3')}
                          />
                        </label>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <FaImage className="text-green-500" />
                          <span className="text-sm text-gray-600">
                            {formData.image3.name} uploaded
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRemoveFile('image3')}
                            className="text-red-600 hover:text-red-800"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  {errors.image2 && (
                    <p className="mt-1 text-sm text-red-600">{errors.image2}</p>
                  )}
                  {errors.image3 && (
                    <p className="mt-1 text-sm text-red-600">{errors.image3}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Product Video
                  </label>
                  <div className="mt-1 flex items-center space-x-4">
                    {!formData.video ? (
                      <label className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-yellow-500 hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 cursor-pointer">
                        <FaUpload className="mr-2" />
                        Upload Video
                        <input
                          type="file"
                          className="hidden"
                          accept=".mp4"
                          onChange={(e) => handleFileUpload(e, 'video')}
                        />
                      </label>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <FaVideo className="text-green-500" />
                        <span className="text-sm text-gray-600">
                          {formData.video.name} uploaded
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveFile('video')}
                          className="text-red-600 hover:text-red-800"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    )}
                  </div>
                  {errors.video && (
                    <p className="mt-1 text-sm text-red-600">{errors.video}</p>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => navigate('/wholesaler/inventory')}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-yellow-500 hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Adding Product...
                    </>
                  ) : (
                    'Add Product'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default WholesalerAddProduct; 